import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Check, Clock } from "lucide-react";
import { SITE } from "@/config";
import { getPurchase } from "@/lib/api";

const paymentLine = (r) => {
  if (r.payment_method === "card") return `Card ending ${r.card_last4 || "····"} — approved`;
  if (r.payment_method === "stripe") {
    if (r.paid) return "Card (Stripe) — approved";
    if (r.stripe_status === "failed") return "Card (Stripe) — failed";
    if (r.stripe_status === "expired") return "Card (Stripe) — expired";
    return "Card (Stripe) — processing";
  }
  if (r.payment_method === "gocardless") {
    if (r.gocardless_status === "confirmed") return "Bank (PayTo) — confirmed";
    if (r.gocardless_status === "failed") return "Bank (PayTo) — failed";
    if (r.gocardless_status === "cancelled") return "Bank (PayTo) — cancelled";
    return "Bank (PayTo) — waiting on your bank";
  }
  return r.paid
    ? "Osko — received"
    : `Osko — please transfer using reference ${r.reference}`;
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!ref) { setLoading(false); return; }
    try {
      const data = await getPurchase(ref);
      setRecord(data);
    } catch {
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => { load(); }, [load]);

  // Poll for up to 90s while payment is settling (Stripe webhook).
  useEffect(() => {
    if (!record || record.paid) return;
    if (record.payment_method === "gocardless" && ["failed", "cancelled"].includes(record.gocardless_status)) return;
    if (record.payment_method === "stripe" && ["failed", "expired"].includes(record.stripe_status)) return;
    const timer = setInterval(load, 3000);
    const stop = setTimeout(() => clearInterval(timer), 90000);
    return () => { clearInterval(timer); clearTimeout(stop); };
  }, [record, load]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#F5F5F5]"><Loader2 className="w-6 h-6 animate-spin text-[#C77B7B]" /></div>;
  }

  const failedGc = record && record.payment_method === "gocardless" && ["failed", "cancelled"].includes(record.gocardless_status);
  const failedStripe = record && record.payment_method === "stripe" && ["failed", "expired"].includes(record.stripe_status);
  const failed = failedGc || failedStripe;
  const awaiting = record && !record.paid && !failed;
  const isOsko = record && record.payment_method === "osko";

  const heading = awaiting
    ? (isOsko ? "Details received." : "Almost done.")
    : failed
      ? "Payment didn't complete."
      : "Thank you.";

  const subheading = awaiting
    ? (isOsko
        ? "Please complete your bank transfer using the details below. We'll email you your ticket number as soon as the payment lands."
        : "We're confirming your payment. This page updates automatically.")
    : failed
      ? "Your payment didn't go through. You can try again from the home page."
      : record?.name ? `We've received your details, ${record.name.split(" ")[0]}.` : "We've received your details.";

  const paid = record && record.paid;

  return (
    <div data-testid="thankyou-page" className="min-h-screen bg-[#F5F5F5] px-6 py-14 md:py-20 flex items-start justify-center">
      <div className="w-full max-w-xl bg-white border border-[#DDE1E4] rounded-2xl p-8 md:p-10 text-center">
        {paid ? (
          <>
            {/* Family photo in the site's arch frame, with confirmation badge */}
            <div className="relative mx-auto w-44">
              <div className="rounded-t-[999px] rounded-b-2xl overflow-hidden border border-[#DDE1E4]">
                <img
                  src={SITE.heroPhoto}
                  alt="Casey and her family"
                  data-testid="thankyou-photo"
                  className="w-full h-52 object-cover"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#C77B7B] grid place-items-center border-4 border-white">
                <Check className="w-5 h-5 text-[#F5F5F5]" strokeWidth={2.5} />
              </div>
            </div>
            <h1 className="mt-8 font-serif-display text-4xl md:text-5xl text-[#333333]">
              Thank you{record.name ? `, ${record.name.split(" ")[0]}` : ""}.
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-[#546E7A]">
              Your generosity means the world to Casey and her family — every dollar goes directly
              towards her treatment and caring for her two little boys.
            </p>
            {record.purchase_number && (
              <div className="mt-6 p-5 rounded-xl bg-[#ECEFF1] border border-[#DDE1E4]">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#546E7A]">Your ticket number</p>
                <p data-testid="ticket-number" className="mt-1 font-serif-display text-5xl text-[#C77B7B]">#{record.purchase_number}</p>
                <p data-testid="email-note" className="mt-2 text-xs text-[#546E7A]">
                  Emailed to <span className="text-[#333333] font-medium">{record.email}</span> — good luck in the draw!
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={`w-14 h-14 rounded-full ${awaiting ? "bg-[#ECEFF1]" : "bg-[#D9C1C1]"} grid place-items-center mx-auto`}>
              {awaiting ? (
                <Clock className="w-6 h-6 text-[#546E7A]" strokeWidth={2} />
              ) : (
                <Check className="w-6 h-6 text-[#F5F5F5]" strokeWidth={2} />
              )}
            </div>
            <h1 className="mt-6 font-serif-display text-4xl md:text-5xl text-[#333333]">{heading}</h1>
            <p className="mt-3 text-sm text-[#546E7A]">{subheading}</p>
          </>
        )}

        {/* Osko transfer instructions — only while awaiting */}
        {awaiting && isOsko && (
          <div data-testid="osko-instructions" className="mt-6 p-4 rounded-xl bg-[#ECEFF1] border border-[#DDE1E4] text-left text-sm text-[#333333] space-y-2">
            <div className="flex justify-between"><span className="text-[#546E7A]">Payee</span><span>{SITE.payeeName}</span></div>
            <div className="flex justify-between"><span className="text-[#546E7A]">PayID ({SITE.payIdType})</span><span>{SITE.payId}</span></div>
            <div className="flex justify-between"><span className="text-[#546E7A]">Amount</span><span>${record.total_amount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#546E7A]">Reference</span><span className="font-medium text-[#333333]">{record.reference}</span></div>
            <p className="text-xs text-[#546E7A] pt-2 border-t border-[#DDE1E4]">
              Include the reference above so we can match your payment.
            </p>
          </div>
        )}

        {record && (
          <div data-testid="summary" className="mt-8 text-left divide-y divide-[#DDE1E4] border border-[#DDE1E4] rounded-xl overflow-hidden">
            {[
              ["Ticket #", record.purchase_number ? `#${record.purchase_number}` : "issued once payment is confirmed"],
              ["Reference", record.reference],
              ["Name", record.name],
              ["Email", record.email],
              ["Mobile", record.phone],
              ["Quantity", record.quantity],
              ["Total", `$${record.total_amount.toFixed(2)}`],
              ["Payment", paymentLine(record)],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-4 py-3 text-sm gap-3">
                <span className="text-[#546E7A]">{k}</span>
                <span data-testid={`summary-${String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-[#333333] font-medium text-right">{v}</span>
              </div>
            ))}
          </div>
        )}

        <Link
          to="/"
          data-testid="back-btn"
          className="inline-flex mt-8 px-6 py-3 rounded-full bg-[#333333] text-[#F5F5F5] hover:bg-[#4a4a4a] transition-colors"
        >
          Done
        </Link>

        <p className="mt-6 text-xs text-[#546E7A]">
          Questions? Contact {SITE.contactEmail}.
        </p>
      </div>
    </div>
  );
}
